import { startCase } from "./recordMappers";

const FDA_DRUGS_DATASET_KEY = "fda_drugs";

const FDA_DRUGS_REMS_EDIT_FIELDS = [
  {
    key: "rems_status",
    label: "REMS Status",
    defaultValue: "none",
    mode: "select",
    options: [
      { value: "none", label: "none" },
      { value: "active", label: "active" },
      { value: "discontinued", label: "discontinued" },
    ],
  },
  {
    key: "rems_url",
    label: "REMS URL",
    defaultValue: "",
    mode: "text",
  },
];

const EDITING_META_FIELDS = new Set([
  "_dataset_key",
  "_dataset_name",
  "_record_id",
  "admin_last_edited_at",
  "admin_last_edited_by",
  "admin_last_reviewed_at",
  "admin_last_reviewed_by",
  "changed_fields",
  "created_at",
  "data_version",
  "has_edits",
  "ingested_at",
  "is_edited",
  "latest_version",
  "last_updated_at",
  "last_updated_by",
  "synced_at",
  "updated",
  "updated_at",
  "__v",
]);

const OMIT_EDITABLE_VALUE = Symbol("omit-editable-value");

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isComplexValue = (value) => Array.isArray(value) || isPlainObject(value);

const isEditableFieldKey = (key) =>
  typeof key === "string" &&
  !key.startsWith("_") &&
  !EDITING_META_FIELDS.has(key) &&
  !EDITING_HIDDEN_FIELD_KEYS.has(normalizeFieldToken(key));

const normalizeFieldToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const EDITING_HIDDEN_FIELD_KEYS = new Set(
  ["doc_status", "openfda", "open_fda", "publication_number"].map((value) =>
    normalizeFieldToken(value)
  )
);

const LARGE_EDITABLE_FIELD_KEYS = new Set(
  [
    "ipc_codes",
    "cpc_codes",
    "openfda",
    "openfda_fei_number",
    "openfda_registration_number",
    "supplement_details",
    "supplemental_submissions",
    "submissions",
    "latest_submission",
    "results",
    "nui",
    "Author_Affiliations_List",
    "locations",
    "location",
  ].map((value) => normalizeFieldToken(value))
);

const isLargeEditableField = (entry) => {
  if (!entry) {
    return false;
  }

  const keyToken = normalizeFieldToken(entry.key);
  const labelToken = normalizeFieldToken(entry.label);

  return (
    LARGE_EDITABLE_FIELD_KEYS.has(keyToken) ||
    LARGE_EDITABLE_FIELD_KEYS.has(labelToken)
  );
};

const normalizeComparableValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce((normalizedValue, key) => {
      normalizedValue[key] = normalizeComparableValue(value[key]);
      return normalizedValue;
    }, {});
};

const areComparableValuesEqual = (leftValue, rightValue) =>
  JSON.stringify(normalizeComparableValue(leftValue)) ===
  JSON.stringify(normalizeComparableValue(rightValue));

const sanitizeEditableValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(sanitizeEditableValue)
      .filter((item) => item !== OMIT_EDITABLE_VALUE);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const sanitizedObject = Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => isEditableFieldKey(key))
      .map(([key, fieldValue]) => [key, sanitizeEditableValue(fieldValue)])
      .filter(([, fieldValue]) => fieldValue !== OMIT_EDITABLE_VALUE)
  );

  return Object.keys(sanitizedObject).length ? sanitizedObject : OMIT_EDITABLE_VALUE;
};

const formatEditableTextValue = (value) =>
  isComplexValue(value)
    ? JSON.stringify(value, null, 2)
    : value === null || value === undefined
      ? ""
      : String(value);

const buildEditableListItem = (value) => ({
  mode: isComplexValue(value) ? "json" : "text",
  templateValue: value,
  textValue: formatEditableTextValue(value),
});

const buildEditableObjectField = (key, value) => ({
  keyName: key,
  mode: isComplexValue(value) ? "json" : "text",
  templateValue: value,
  textValue: formatEditableTextValue(value),
});

const buildCompactPreview = (value, maxLength = 48) => {
  const normalized = formatEditableTextValue(value).replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "Empty";
  }

  return normalized.length > maxLength
    ? `${normalized.slice(0, Math.max(0, maxLength - 3))}...`
    : normalized;
};

const normalizeFdaDrugsRemsStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return FDA_DRUGS_REMS_EDIT_FIELDS[0].options.some((option) => option.value === normalized)
    ? normalized
    : "none";
};

const buildEditableEntries = (record, datasetKey = "") => {
  const editableRecord = sanitizeEditableValue(record || {});
  if (editableRecord === OMIT_EDITABLE_VALUE) {
    if (datasetKey !== FDA_DRUGS_DATASET_KEY) {
      return [];
    }
  }

  const entries = Object.entries(
    editableRecord === OMIT_EDITABLE_VALUE ? {} : editableRecord
  )
    .filter(([key]) => isEditableFieldKey(key))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => {
      const label = startCase(key);
      const mode = Array.isArray(value)
        ? "array"
        : isPlainObject(value)
          ? "object"
          : typeof value === "string" && value.length > 140
            ? "textarea"
            : "text";

      return {
        key,
        label,
        value,
        editorVariant:
          mode === "array" && isLargeEditableField({ key, label })
            ? "large-array"
            : mode === "object" && isLargeEditableField({ key, label })
              ? "large-object"
              : mode === "textarea" && isLargeEditableField({ key, label })
                ? "large-text"
                : "default",
        mode,
        textValue: formatEditableTextValue(value),
        items: Array.isArray(value) ? value.map(buildEditableListItem) : undefined,
        fields: isPlainObject(value)
          ? Object.entries(value).map(([fieldKey, fieldValue]) =>
              buildEditableObjectField(fieldKey, fieldValue)
            )
          : undefined,
      };
    });

  if (datasetKey !== FDA_DRUGS_DATASET_KEY) {
    return entries;
  }

  const remsEntries = FDA_DRUGS_REMS_EDIT_FIELDS.map((fieldConfig) => {
    const existingEntry = entries.find((entry) => entry.key === fieldConfig.key);
    const rawValue = existingEntry?.value ?? fieldConfig.defaultValue;
    const value =
      fieldConfig.key === "rems_status" ? normalizeFdaDrugsRemsStatus(rawValue) : rawValue;

    return {
      key: fieldConfig.key,
      label: fieldConfig.label,
      value,
      mode: fieldConfig.mode,
      textValue: value === null || value === undefined ? "" : String(value),
      options: fieldConfig.options,
    };
  });

  const remsEntryKeys = new Set(remsEntries.map((entry) => entry.key));
  return [...remsEntries, ...entries.filter((entry) => !remsEntryKeys.has(entry.key))];
};

const buildEntryFormValue = (entry) => {
  if (entry.mode === "array") {
    return entry.items || [];
  }

  if (entry.mode === "object") {
    return entry.fields || [];
  }

  return entry.textValue;
};

const looksLikeJsonValue = (value) => {
  const trimmedValue = value.trim();
  return (
    (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
    (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"))
  );
};

const parseValueByTemplate = ({ label, templateValue, rawValue }) => {
  if (isComplexValue(templateValue)) {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return Array.isArray(templateValue) ? [] : {};
    }

    try {
      return JSON.parse(trimmedValue);
    } catch {
      throw new Error(`${label} must contain valid JSON.`);
    }
  }

  if (typeof templateValue === "number") {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return "";
    }

    const parsedNumber = Number(trimmedValue);
    if (Number.isNaN(parsedNumber)) {
      throw new Error(`${label} must be a valid number.`);
    }
    return parsedNumber;
  }

  if (typeof templateValue === "boolean") {
    const normalizedValue = rawValue.trim().toLowerCase();
    if (normalizedValue === "true") {
      return true;
    }
    if (normalizedValue === "false") {
      return false;
    }
    throw new Error(`${label} must be true or false.`);
  }

  const trimmedValue = rawValue.trim();
  if (templateValue === undefined && looksLikeJsonValue(trimmedValue)) {
    try {
      return JSON.parse(trimmedValue);
    } catch {
      throw new Error(`${label} must contain valid JSON.`);
    }
  }

  return rawValue;
};

const parseEditedArrayValue = (entry, rawItems) =>
  (rawItems || []).map((item, index) =>
    parseValueByTemplate({
      label: `${entry.label} item ${index + 1}`,
      templateValue: item.templateValue,
      rawValue: item.textValue || "",
    })
  );

const parseEditedObjectValue = (entry, rawFields) => {
  const nextObjectValue = {};

  (rawFields || []).forEach((field, index) => {
    const keyName = String(field.keyName || "").trim();
    if (!keyName) {
      throw new Error(`${entry.label} field ${index + 1} needs a field name.`);
    }

    nextObjectValue[keyName] = parseValueByTemplate({
      label: `${entry.label} field "${keyName}"`,
      templateValue: field.templateValue,
      rawValue: field.textValue || "",
    });
  });

  return nextObjectValue;
};

const parseEditedValue = (entry, rawValue) => {
  if (entry.mode === "array") {
    return parseEditedArrayValue(entry, rawValue);
  }

  if (entry.mode === "object") {
    return parseEditedObjectValue(entry, rawValue);
  }

  return parseValueByTemplate({
    label: entry.label,
    templateValue: entry.value,
    rawValue,
  });
};

const buildEditableBaselineRecord = (editableEntries) =>
  editableEntries.reduce((baselineRecord, entry) => {
    baselineRecord[entry.key] = parseEditedValue(entry, buildEntryFormValue(entry));
    return baselineRecord;
  }, {});

export {
  EDITING_META_FIELDS,
  OMIT_EDITABLE_VALUE,
  LARGE_EDITABLE_FIELD_KEYS,
  FDA_DRUGS_DATASET_KEY,
  FDA_DRUGS_REMS_EDIT_FIELDS,
  areComparableValuesEqual,
  buildCompactPreview,
  buildEditableBaselineRecord,
  buildEditableEntries,
  buildEditableListItem,
  buildEditableObjectField,
  buildEntryFormValue,
  formatEditableTextValue,
  isComplexValue,
  isEditableFieldKey,
  isLargeEditableField,
  isPlainObject,
  looksLikeJsonValue,
  normalizeComparableValue,
  normalizeFieldToken,
  parseEditedArrayValue,
  parseEditedObjectValue,
  parseEditedValue,
  parseValueByTemplate,
  sanitizeEditableValue,
};
